<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import VoxelViewport from './components/VoxelViewport.vue'
import { api } from './lib/api'
import { readToken, requireLogin } from './lib/auth'
import { useEditorStore, type Tool, type VoxelDocument } from './stores/editor'

const editor = useEditorStore()
const importInput = ref<HTMLInputElement>()
const remoteUrl = ref('')
const showImport = ref(false)
const remoteBusy = ref(false)
const tools: Array<{ id: Tool; label: string; key: string }> = [
  { id: 'orbit', label: 'Navigate', key: '1' }, { id: 'paint', label: 'Paint', key: '2' },
  { id: 'erase', label: 'Erase', key: '3' }, { id: 'select', label: 'Select', key: '4' }, { id: 'eyedropper', label: 'Sample', key: '5' },
]
const palette = computed(() => Object.entries(editor.document.colors))

function keyboard(event: KeyboardEvent) {
  if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'z') { event.preventDefault(); event.shiftKey ? editor.redo() : editor.undo(); return }
  const match = tools.find((item) => item.key === event.key); if (match) editor.tool = match.id
}

async function localImport(event: Event) {
  const file = (event.target as HTMLInputElement).files?.[0]; if (!file) return
  try {
    if (file.name.endsWith('.json')) editor.load(JSON.parse(await file.text()) as VoxelDocument, file.name.replace(/\.json$/i, ''))
    else editor.status = 'VFP uploads are validated and opened through a saved project.'
  } catch { editor.status = 'Could not read the selected voxel JSON.' }
}

async function importRemote() {
  if (!remoteUrl.value) return
  remoteBusy.value = true
  try {
    const projects = await api<Array<{ id: string }>>('/projects')
    if (!projects[0]) throw new Error('Create a cloud project before importing a CDN asset.')
    const result = await api<{ document: VoxelDocument; filename: string }>(`/projects/${projects[0].id}/assets/remote`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ url: remoteUrl.value }),
    })
    editor.load(result.document, result.filename); showImport.value = false
  } catch (error) { editor.status = error instanceof Error ? error.message : 'Remote import failed' }
  finally { remoteBusy.value = false }
}

function exportJson() {
  const blob = new Blob([JSON.stringify(editor.document, null, 2)], { type: 'application/json' })
  const link = document.createElement('a'); link.href = URL.createObjectURL(blob); link.download = `${editor.projectName}.json`; link.click(); URL.revokeObjectURL(link.href)
  editor.status = 'Exported PixForge JSON'
}

onMounted(() => { readToken(); window.addEventListener('keydown', keyboard); if (import.meta.env.VITE_AUTH_APP_ID && import.meta.env.VITE_AUTH_APP_ID !== 'replace-at-deploy') requireLogin() })
</script>

<template>
  <main class="studio" :class="{ focused: editor.isFocused }">
    <header class="topbar">
      <div class="brand"><span class="brand-mark">XZ</span><span>XiangZao <b>Studio</b></span></div>
      <div class="project-title"><span class="status-dot"></span><strong>{{ editor.projectName }}</strong><span class="muted">{{ editor.occupied.toLocaleString() }} voxels</span></div>
      <div class="top-actions">
        <button class="ghost" @click="editor.undo" :disabled="!editor.undoStack.length">Undo</button>
        <button class="ghost" @click="editor.redo" :disabled="!editor.redoStack.length">Redo</button>
        <button class="ghost" @click="showImport = true">Import</button>
        <button class="primary" @click="exportJson">Export</button>
      </div>
    </header>

    <section class="workspace">
      <aside class="toolrail" aria-label="Editor tools">
        <button v-for="tool in tools" :key="tool.id" class="tool" :class="{ active: editor.tool === tool.id }" :title="`${tool.label} (${tool.key})`" @click="editor.tool = tool.id">
          <span>{{ tool.label.slice(0, 1) }}</span><small>{{ tool.key }}</small>
        </button>
        <div class="rail-spacer"></div>
        <button class="tool" :class="{ active: editor.isFocused }" title="Focus mode" @click="editor.isFocused = !editor.isFocused"><span>F</span></button>
      </aside>

      <section class="canvas-panel">
        <VoxelViewport />
        <div class="viewport-hud"><span>Perspective</span><span>Orbit</span><span>Grid 1</span></div>
        <div class="tool-hint"><b>{{ editor.tool }}</b><span>Click a voxel to edit</span></div>
      </section>

      <aside class="inspector">
        <section class="panel-section">
          <div class="panel-heading"><span>Layers</span><button class="icon-button" title="Add layer">+</button></div>
          <div class="layer active-layer"><span class="eye">●</span><span class="layer-color"></span><strong>Model</strong><span class="muted">{{ editor.occupied }}</span></div>
          <div class="layer"><span class="eye">○</span><span class="layer-color secondary"></span><span>Reference</span><span class="muted">locked</span></div>
        </section>
        <section class="panel-section palette-section">
          <div class="panel-heading"><span>Palette</span><button class="icon-button" title="Add color" @click="editor.addPalette('#65B8FF')">+</button></div>
          <div class="palette">
            <button v-for="[symbol, color] in palette" :key="symbol" class="swatch" :class="{ selected: editor.selectedSymbol === symbol }" :style="{ backgroundColor: color }" :title="`${symbol}: ${color}`" @click="editor.selectedSymbol = symbol"><span>{{ symbol }}</span></button>
          </div>
          <div class="color-readout"><span class="selected-colour" :style="{ backgroundColor: editor.document.colors[editor.selectedSymbol] }"></span><div><strong>{{ editor.selectedSymbol }}</strong><small>{{ editor.document.colors[editor.selectedSymbol] }}</small></div><button class="ghost small">Material</button></div>
        </section>
        <section class="panel-section">
          <div class="panel-heading"><span>Scene</span><button class="icon-button" title="Scene presets">•••</button></div>
          <label class="field"><span>Lighting</span><input type="range" min="0" max="100" value="68" /></label>
          <label class="field"><span>Background</span><select><option>Studio charcoal</option><option>Neutral light</option><option>Transparent</option></select></label>
          <label class="field"><span>Render</span><select><option>Physically based</option><option>Clay preview</option><option>Albedo</option></select></label>
        </section>
      </aside>
    </section>

    <footer class="statusbar"><span class="connection" :class="{ online: editor.connected }"></span><span>{{ editor.connected ? 'Live collaboration' : 'Local preview' }}</span><span class="divider"></span><span>{{ editor.status }}</span><span class="footer-spacer"></span><span>{{ editor.selected.size }} selected</span><span class="divider"></span><span>128³ workspace</span></footer>

    <div v-if="showImport" class="modal-backdrop" @click.self="showImport = false">
      <section class="modal" aria-modal="true" role="dialog">
        <div class="modal-heading"><div><span class="eyebrow">ASSET INTAKE</span><h1>Open voxel asset</h1></div><button class="icon-button close" @click="showImport = false">×</button></div>
        <button class="dropzone" @click="importInput?.click()"><strong>Choose a JSON or VFP file</strong><span>VFP packages are validated by the server before opening.</span></button>
        <input ref="importInput" hidden type="file" accept=".json,.vfp,application/json" @change="localImport" />
        <div class="or"><span></span>or import from URL<span></span></div>
        <label class="url-input"><span>HTTPS / CDN asset URL</span><input v-model.trim="remoteUrl" placeholder="https://cdn.example.com/model.vfp" @keydown.enter="importRemote" /></label>
        <div class="modal-actions"><span class="muted">Private ranges and metadata endpoints are blocked.</span><button class="primary" :disabled="remoteBusy || !remoteUrl" @click="importRemote">{{ remoteBusy ? 'Checking…' : 'Import URL' }}</button></div>
      </section>
    </div>
  </main>
</template>

